import { memo, useCallback, useEffect, useRef } from 'react';

import { type CommentResponse } from '@air/api/types';
import { useBreakpointsContext } from '@air/provider-media-query';

import { CommentDisplay, type CommentDisplayProps } from '~/components/DiscussionsV2/CommentDisplay';
import {
  DiscussionForm,
  type DiscussionFormHandle,
  type MentionsData,
} from '~/components/DiscussionsV2/DiscussionForm';
import { DISCUSSION_COMMENT_BODY, EDIT_COMMENT_FORM } from '~/constants/testIDs';

export type DiscussionCommentContentProps = {
  comment: CommentResponse;
  mentionsList: MentionsData[];
  isLoadingMentions?: boolean;
  onSave?: (newComment: string) => void;
  shouldShowMentionsButton?: boolean;
  isEditing: boolean;
  cancelEditing: () => void;
  metadata?: CommentDisplayProps['metadata'];
};

export const DiscussionCommentContent = memo(
  ({
    comment,
    isLoadingMentions,
    mentionsList,
    onSave,
    shouldShowMentionsButton,
    isEditing,
    cancelEditing,
    metadata,
  }: DiscussionCommentContentProps) => {
    const commentForm = useRef<DiscussionFormHandle>(null);

    const { isAboveMediumScreen } = useBreakpointsContext();

    useEffect(() => {
      if (isEditing) {
        commentForm.current?.focus();
      }
    }, [isEditing]);

    const onCommentSubmit = useCallback(
      (body: string) => {
        onSave?.(body);
        cancelEditing();
      },
      [cancelEditing, onSave],
    );

    return (
      <>
        {isEditing && onSave ? (
          <div className="discussion-form-container">
            <DiscussionForm
              data-testid={EDIT_COMMENT_FORM}
              ref={commentForm}
              comment={comment.body}
              hasCancelButton={isEditing && !isAboveMediumScreen}
              isLoadingMentions={isLoadingMentions}
              mentionsList={mentionsList}
              onCancel={cancelEditing}
              onSubmit={onCommentSubmit}
              shouldShowMentionsButton={isAboveMediumScreen && shouldShowMentionsButton}
            />
          </div>
        ) : (
          <div className="mr-4 flex w-fit">
            <CommentDisplay
              className="select-text"
              metadata={metadata}
              text={comment.body}
              data-testid={DISCUSSION_COMMENT_BODY}
              mentionsList={mentionsList}
            />
          </div>
        )}
      </>
    );
  },
);

DiscussionCommentContent.displayName = 'DiscussionCommentContent';
